/**
 * Skills-Extractor Widget — LLM-powered.
 *
 * Employers paste a job description (or type a role name), we call
 * POST /api/discover/extract-skills-from-jd, and render:
 *   · must-have + nice-to-have skill chips (click to add to the parent
 *     search's skills filter via `onApplySkills`)
 *   · trending skills (industry-hot, one line each)
 *   · "Recruiter Playbook" panel — experience signals, red flags,
 *     certifications, review tips, sharp interview probes
 *
 * Available on both /discover (quick search) and /employer/store
 * (Candidates Store). Pending employers can use it too — it's a
 * discovery aid, not a data-egress point.
 */
import React, { useState, useCallback } from "react";
import axios from "axios";
import {
  Sparkles, ChevronDown, ChevronUp, Plus, Loader, AlertTriangle,
  ClipboardCheck, Flame, Shield, Search,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const MUSTARD = "#FFC145";

function SkillChip({ label, tone = "primary", onAdd, dataTestId }) {
  const styles = {
    primary:  { bg: "#FFF2EC", color: NAVY,    border: ORANGE, dot: ORANGE },
    nice:     { bg: "#F1F5F9", color: "#334155", border: "#CBD5E1", dot: "#94A3B8" },
    trending: { bg: "#FEF3C7", color: "#78350F", border: "#F59E0B", dot: "#F59E0B" },
  }[tone] || {};
  return (
    <button
      type="button"
      onClick={onAdd}
      data-testid={dataTestId}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded transition-transform hover:translate-y-[-1px]"
      style={{
        background: styles.bg, color: styles.color,
        border: `1.5px solid ${styles.border}`,
        boxShadow: `2px 2px 0 0 ${styles.dot}22`,
        fontWeight: 700, cursor: onAdd ? "pointer" : "default",
      }}
      title={onAdd ? "Add to skills filter" : ""}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: styles.dot }} />
      {label}
      {onAdd && <Plus size={11} style={{ color: styles.dot }} />}
    </button>
  );
}

export default function SkillsExtractorWidget({
  onApplySkills = () => {},
  compact = false,
  defaultOpen = false,
  testIdPrefix = "skills-extractor",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [jdText, setJdText] = useState("");
  const [roleName, setRoleName] = useState("");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [playbookOpen, setPlaybookOpen] = useState(false);

  const runExtract = useCallback(async () => {
    if (!jdText.trim() && !roleName.trim()) {
      setError("Paste a job description or type a role name.");
      return;
    }
    setBusy(true);
    setError("");
    setData(null);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/discover/extract-skills-from-jd`,
        { jd_text: jdText, role_name: roleName, industry },
        { withCredentials: true, timeout: 90000 },
      );
      setData(r.data);
      setPlaybookOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Extraction failed. Please retry.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [jdText, roleName, industry]);

  const applyAllMustHaves = () => {
    if (!data?.must_have_skills?.length) return;
    onApplySkills(data.must_have_skills.map((s) => s.name).filter(Boolean));
  };
  const applyAll = () => {
    if (!data?.search_query_suggestion) return;
    onApplySkills(
      data.search_query_suggestion.split(",").map((s) => s.trim()).filter(Boolean),
    );
  };

  return (
    <div
      className="rounded-md overflow-hidden"
      style={{
        background: "white",
        border: `1.5px solid ${NAVY}`,
        boxShadow: `3px 3px 0 0 ${MUSTARD}`,
      }}
      data-testid={`${testIdPrefix}-root`}
    >
      {/* Header — collapsible toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        style={{ background: NAVY, color: "white" }}
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: MUSTARD }} />
          <div className="text-left">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: MUSTARD, fontWeight: 700 }}>
              · Resmo · Skills Extractor
            </div>
            <div className="font-display uppercase leading-tight" style={{ fontSize: compact ? "0.95rem" : "1.05rem", letterSpacing: "-0.01em" }}>
              Paste a JD — get the market-hot skills + recruiter playbook.
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="p-4 md:p-5" data-testid={`${testIdPrefix}-body`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <label className="block md:col-span-2">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                · Paste job description
              </div>
              <textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                rows={compact ? 3 : 5}
                placeholder="Paste the JD text here — Resmo will extract the must-haves, spot the market-hot skills, and give you an interview playbook…"
                className="w-full rounded p-2 text-[12px] leading-snug"
                style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                data-testid={`${testIdPrefix}-jd`}
              />
            </label>
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                  · Role name
                </div>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Senior Product Analyst"
                  className="w-full rounded px-3 py-2 text-[13px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid={`${testIdPrefix}-role`}
                />
              </label>
              <label className="block">
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                  · Industry (optional)
                </div>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g. Fintech, Healthcare"
                  className="w-full rounded px-3 py-2 text-[13px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid={`${testIdPrefix}-industry`}
                />
              </label>
              <button
                type="button"
                onClick={runExtract}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                style={{
                  background: ORANGE, color: "white",
                  border: `2px solid ${NAVY}`,
                  boxShadow: `3px 3px 0 0 ${NAVY}`,
                  fontWeight: 700, cursor: busy ? "wait" : "pointer",
                  opacity: busy ? 0.7 : 1,
                }}
                data-testid={`${testIdPrefix}-run`}
              >
                {busy ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {busy ? "Extracting… (~30–60s)" : "Extract with AI"}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="flex items-center gap-2 text-[12px] rounded p-2 mb-3"
              style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#7F1D1D" }}
              data-testid={`${testIdPrefix}-error`}
            >
              <AlertTriangle size={12} /> {error}
            </div>
          )}

          {data && (
            <div data-testid={`${testIdPrefix}-results`}>
              {/* Role family + seniority pill row */}
              <div className="flex items-center flex-wrap gap-2 mb-3">
                {data.role_family && (
                  <span
                    className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                    style={{ background: NAVY, color: MUSTARD, fontWeight: 700 }}
                    data-testid={`${testIdPrefix}-family`}
                  >
                    · {data.role_family}
                  </span>
                )}
                {data.seniority_hint && (
                  <span
                    className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                    style={{ background: "#F1F5F9", color: NAVY, border: "1px solid #CBD5E1", fontWeight: 700 }}
                  >
                    · {data.seniority_hint}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {data.must_have_skills?.length > 0 && (
                    <button
                      type="button"
                      onClick={applyAllMustHaves}
                      className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                      style={{ background: ORANGE, color: "white", fontWeight: 700, border: `1.5px solid ${NAVY}` }}
                      data-testid={`${testIdPrefix}-apply-must`}
                    >
                      <Search size={10} /> Filter by must-haves
                    </button>
                  )}
                  {data.search_query_suggestion && (
                    <button
                      type="button"
                      onClick={applyAll}
                      className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                      style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, fontWeight: 700 }}
                      data-testid={`${testIdPrefix}-apply-suggestion`}
                    >
                      <Plus size={10} /> Apply full query
                    </button>
                  )}
                </div>
              </div>

              {/* Must-have chips */}
              {data.must_have_skills?.length > 0 && (
                <div className="mb-3">
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5" style={{ color: ORANGE, fontWeight: 700 }}>
                    · Must-have skills
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.must_have_skills.map((s, i) => (
                      <SkillChip
                        key={`must-${i}`}
                        label={s.name}
                        tone="primary"
                        onAdd={() => onApplySkills([s.name])}
                        dataTestId={`${testIdPrefix}-must-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Nice-to-have chips */}
              {data.nice_to_have_skills?.length > 0 && (
                <div className="mb-3">
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#64748B", fontWeight: 700 }}>
                    · Nice-to-have skills
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.nice_to_have_skills.map((s, i) => (
                      <SkillChip
                        key={`nice-${i}`}
                        label={s.name}
                        tone="nice"
                        onAdd={() => onApplySkills([s.name])}
                        dataTestId={`${testIdPrefix}-nice-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Trending / industry-hot chips */}
              {data.trending_skills?.length > 0 && (
                <div className="mb-3">
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5 inline-flex items-center gap-1" style={{ color: "#B45309", fontWeight: 700 }}>
                    <Flame size={10} /> · Industry-hot skills
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.trending_skills.map((s, i) => (
                      <SkillChip
                        key={`trending-${i}`}
                        label={s.name}
                        tone="trending"
                        onAdd={() => onApplySkills([s.name])}
                        dataTestId={`${testIdPrefix}-trending-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Recruiter Playbook (collapsible) */}
              {data.recruiter_insights && (
                <div
                  className="rounded-md overflow-hidden mt-3"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid={`${testIdPrefix}-playbook`}
                >
                  <button
                    type="button"
                    onClick={() => setPlaybookOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-3 py-2"
                    style={{ background: "#F1F5F9" }}
                    data-testid={`${testIdPrefix}-playbook-toggle`}
                  >
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono-rm uppercase tracking-widest" style={{ color: NAVY, fontWeight: 700 }}>
                      <ClipboardCheck size={12} style={{ color: ORANGE }} /> · Recruiter Playbook
                    </span>
                    {playbookOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                  {playbookOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
                      <PlaybookList
                        title="Experience signals"
                        items={data.recruiter_insights.experience_signals}
                        color={NAVY}
                        testId={`${testIdPrefix}-playbook-exp`}
                      />
                      <PlaybookList
                        title="Red flags"
                        items={data.recruiter_insights.red_flags}
                        color="#B91C1C"
                        icon={<AlertTriangle size={10} />}
                        testId={`${testIdPrefix}-playbook-flags`}
                      />
                      <PlaybookList
                        title="Certifications worth noting"
                        items={data.recruiter_insights.certifications}
                        color="#0E5A4A"
                        icon={<Shield size={10} />}
                        testId={`${testIdPrefix}-playbook-certs`}
                      />
                      <PlaybookList
                        title="How to review the résumé"
                        items={data.recruiter_insights.review_tips}
                        color="#7C3AED"
                        testId={`${testIdPrefix}-playbook-review`}
                      />
                      <div className="md:col-span-2">
                        <PlaybookList
                          title="Sharp interview probes"
                          items={data.recruiter_insights.interview_probes}
                          color={ORANGE}
                          testId={`${testIdPrefix}-playbook-probes`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlaybookList({ title, items, color, icon, testId }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <div
        className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5 inline-flex items-center gap-1"
        style={{ color, fontWeight: 700 }}
      >
        {icon || null} · {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className="text-[12px] leading-snug pl-3 relative"
            style={{ color: "#334155" }}
          >
            <span style={{ position: "absolute", left: 0, top: 6, width: 4, height: 4, borderRadius: "50%", background: color }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
